import qs from 'qs';
import { useEffect, useState } from 'react';

import useInviteToken from '../../auth/context/useInviteToken';

export type InviteSignup = {
  /** The inviting org's name, once resolved — drives the "Sign up and join {org}" heading. */
  orgTitle: string | null;
  /** The address the invite was issued to; the email field is prefilled and locked to it. */
  invitedEmail: string | null;
  /** Failure copy from a token that could not be resolved (expired / invalid). */
  inviteAlert: string | null;
  /** The invite resolved: the address is fixed, so the member-org lookup is skipped. */
  isEmailLocked: boolean;
  /**
   * The token failed to resolve. The alert is shown, but progression is blocked so the user can't retype a
   * different address and dead-end at the same error on submit — they take the sign-up-without-an-invite link.
   */
  isInviteInvalid: boolean;
  /**
   * Still resolving: neither the invited email (success) nor an alert (failure) has landed. Submitting in
   * this window would let a hand-typed email race the invite result, which then overwrites the field mid-flow.
   */
  isInviteResolving: boolean;
  /** The escape hatch for an unusable invite: the same page without the token, preserving `next`. */
  registerWithoutInviteHref: string;
};

/**
 * Resolves the signup page's `?inviteToken=`, exposing the derived flags the flow gates on. Extracted from
 * the register page so those four booleans are defined once, next to the alert state they depend on, rather
 * than re-derived inline among the page's other concerns.
 *
 * The invited address itself is returned rather than applied: `email` is the page's own state (shared with
 * the later steps), so the page owns prefilling it.
 */
export function useInviteSignup(inviteToken?: string, next?: string): InviteSignup {
  const [inviteAlert, setInviteAlert] = useState<string | null>(null);
  // `setInviteAlert` is a stable setter, safe as the hook's effect dependency (it refetches only when the
  // token changes). It resolves the invite to the org name and the invited address.
  const { orgTitle, invitedEmail } = useInviteToken(setInviteAlert, inviteToken);

  // The sign-up-without-an-invite escape hatch navigates within the mounted page; `useInviteToken` does
  // nothing once the token is gone, so a stale invite alert would otherwise keep rendering.
  useEffect(() => {
    if (!inviteToken) {
      setInviteAlert(null);
    }
  }, [inviteToken]);

  return {
    orgTitle,
    invitedEmail,
    inviteAlert,
    isEmailLocked: Boolean(invitedEmail),
    isInviteInvalid: Boolean(inviteToken && inviteAlert && !invitedEmail),
    isInviteResolving: Boolean(inviteToken) && !invitedEmail && !inviteAlert,
    registerWithoutInviteHref: next ? `/auth/register?${qs.stringify({ next })}` : '/auth/register',
  };
}
